import { makeRule, STYLED_MATCHES, validateRule } from "../stylemap.js";
import { findOverlaps } from "../order.js";
import { ruleName } from "../labels.js";
import { extractMatches } from "../matches.js";
import { buildSpecimen } from "../specimen.js";
import { convert } from "../convert.js";
import { state, ensureWorking, updateWorking } from "../state.js";
import { el, srOnly, actionButton, field, announce, debounce, setBusy } from "../ui/dom.js";
import { chooseUnsaved, confirmAction } from "../ui/dialogs.js";
import { previewFrame, showPreview, codeView } from "../ui/preview.js";

const TYPE_OPTIONS = [
  ["Styles", [["p", "Paragraph style"], ["r", "Character style"], ["table", "Table style"]]],
  ["Lists", [1, 2, 3, 4, 5].flatMap((n) => [[`ul${n}`, `Bulleted list, level ${n}`], [`ol${n}`, `Numbered list, level ${n}`]])],
  ["Formatting", [["b", "Bold"], ["i", "Italic"], ["u", "Underline"], ["strike", "Strikethrough"], ["small-caps", "Small caps"], ["all-caps", "All caps"], ["highlight", "Highlight"]]],
];

function basePicks(match) {
  if (match === "p") return ["p:fresh", "h2:fresh", "h3:fresh", "blockquote > p:fresh", "aside > p:fresh", "pre:separator('\\n')"];
  if (match === "r") return ["strong", "em", "code", "mark", "span"];
  if (match === "table") return ["table"];
  const list = /^(ul|ol)([1-5])$/.exec(match);
  if (list) return [`${"ul|ol > li > ".repeat(Number(list[2]) - 1)}${list[1]} > li:fresh`];
  return ["strong", "em", "u", "s", "mark", "small"];
}

export function mount(root, route, nav) {
  if (!ensureWorking(route.id)) {
    queueMicrotask(() => nav.go("#/profiles"));
    return {};
  }
  const isNew = route.index === "new";
  const profileHash = () => `#/profiles/${encodeURIComponent(state.working.id)}`;
  if (!isNew && !state.working.rules[route.index]) {
    queueMicrotask(() => nav.go(profileHash()));
    return {};
  }
  let rule = isNew
    ? structuredClone(state.ruleDraft?.index === "new" ? state.ruleDraft.rule : makeRule({ match: "p", style: "", html: "p:fresh" }))
    : structuredClone(state.working.rules[route.index]);
  state.ruleDraft = null;
  const initial = JSON.stringify(rule);
  let finished = false;
  let leaveChoice = null; // what this page's guard decided, in case the router then cancels
  const ruleDirty = () => !finished && JSON.stringify(rule) !== initial;

  const withDraft = () => {
    const profile = structuredClone(state.working);
    if (isNew) profile.rules.push(rule);
    else profile.rules[route.index] = rule;
    return profile;
  };
  const draftIndex = (profile) => (isNew ? profile.rules.length - 1 : route.index);

  // ---- Form ----
  const typeSelect = el("select", {}, TYPE_OPTIONS.map(([label, options]) =>
    el("optgroup", { label }, options.map(([value, text]) => el("option", { value }, text)))));
  typeSelect.value = rule.match;

  const styleInput = el("input", { type: "text", value: rule.style ?? "", autocomplete: "off", spellcheck: false });
  const styleError = el("p", { class: "field-error", id: "rule-style-error", hidden: true });
  const prefixBox = el("input", { type: "checkbox", id: "rule-prefix", checked: Boolean(rule.prefix) });
  const styleField = el("div", { class: "field" },
    field({ id: "rule-style", label: "Word style name", hint: "Exactly as shown in Word's Styles pane. For tables, leave empty to match any table.", control: styleInput }),
    styleError,
    el("label", { class: "check" }, prefixBox, el("span", {}, "Also match style names that start with this")));
  styleInput.setAttribute("aria-describedby", "rule-style-hint rule-style-error");

  const htmlInput = el("input", { type: "text", class: "mono", value: rule.html, autocomplete: "off", spellcheck: false });
  const htmlError = el("p", { class: "field-error", id: "rule-html-error", hidden: true });
  const picks = el("div", { class: "picks", role: "group", "aria-label": "Common outputs" });
  const htmlField = el("div", { class: "field" },
    field({
      id: "rule-html", label: "HTML output", control: htmlInput,
      hint: "An element and its classes, e.g. div.alert.alert-info > p:fresh. “>” nests one element inside another; “:fresh” starts a new element for each paragraph. Leave empty to output plain text.",
    }),
    htmlError, picks);
  htmlInput.setAttribute("aria-describedby", "rule-html-hint rule-html-error");

  const plainBox = el("input", { type: "checkbox", id: "rule-plain", checked: rule.group === "quiet" });
  const plainField = el("label", { class: "check" }, plainBox, el("span", {}, "Output as plain text without a warning",
    el("span", { class: "hint block" }, "For styles that only change how text looks in Word.")));

  const autoLabel = el("span");
  const priorityRadio = (value, label) => el("label", {},
    el("input", { type: "radio", name: "priority", value, checked: (rule.priority ?? "auto") === value, onchange: () => update({ priority: value === "auto" ? undefined : value }) }),
    label);
  const priorityBox = el("fieldset", { class: "radios-inline", hidden: true },
    el("legend", {}, "When rules overlap"),
    priorityRadio("auto", autoLabel), priorityRadio("first", "Always win"), priorityRadio("last", "Always lose"));

  function renderPicks() {
    const sameType = state.working.rules.filter((r) => r.match === rule.match && r.group !== "quiet" && r.html).map((r) => r.html);
    const values = [...new Set([...basePicks(rule.match), ...sameType])].slice(0, 10);
    picks.replaceChildren(...values.map((value) => el("button", {
      type: "button", class: "pick mono",
      onclick: () => {
        htmlInput.value = value;
        update({ html: value });
        htmlInput.focus();
      },
    }, srOnly("Use "), value)));
  }

  function renderPriority() {
    const profile = withDraft();
    const index = draftIndex(profile);
    const automatic = structuredClone(profile);
    delete automatic.rules[index].priority;
    const overlap = findOverlaps(automatic.rules).get(index);
    priorityBox.hidden = !overlap;
    if (!overlap) return;
    const others = overlap.with.map((i) => `“${ruleName(profile.rules[i])}”`).join(", ");
    autoLabel.textContent = `Automatic (currently ${overlap.wins ? "wins" : "loses"} against ${others})`;
  }

  function showProblem(problem) {
    for (const [name, input, error] of [["style", styleInput, styleError], ["html", htmlInput, htmlError]]) {
      const mine = problem?.field === name;
      error.textContent = mine ? problem.message : "";
      error.hidden = !mine;
      if (mine) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }
    saveButton.disabled = Boolean(problem);
  }

  function renderForm() {
    const styled = STYLED_MATCHES.includes(rule.match);
    styleField.hidden = !styled;
    heading.textContent = isNew ? "New rule" : `Rule: ${ruleName(rule)}`;
    renderPicks();
    renderPriority();
    showProblem(validateRule(rule));
  }

  function update(changes) {
    // Rebuild from the form so values hidden by a type change (style, prefix) aren't lost.
    rule = makeRule({
      ...rule,
      style: styleInput.value.trim() || undefined,
      prefix: prefixBox.checked,
      html: htmlInput.value.trim(),
      group: plainBox.checked ? "quiet" : "main",
      ...changes,
      sample: isNew ? undefined : rule.sample,
    });
    renderForm();
    schedulePreview();
  }

  typeSelect.addEventListener("change", () => update({ match: typeSelect.value }));
  styleInput.addEventListener("input", () => update({ style: styleInput.value.trim() || undefined }));
  prefixBox.addEventListener("change", () => update({ prefix: prefixBox.checked }));
  htmlInput.addEventListener("input", () => update({ html: htmlInput.value.trim() }));
  plainBox.addEventListener("change", () => update({ group: plainBox.checked ? "quiet" : "main" }));

  // ---- Buttons ----
  const saveButton = actionButton("Save rule", "and return to the profile", { class: "primary-btn", onclick: saveRule });
  const cancelButton = actionButton("Cancel", "discard changes to this rule", {
    onclick: () => {
      finished = true;
      nav.go(profileHash());
    },
  });
  const deleteButton = isNew ? null : actionButton("Delete rule", "", { class: "danger-btn", onclick: deleteRule });

  function commit() {
    const problem = validateRule(rule);
    if (problem) {
      showProblem(problem);
      (problem.field === "style" ? styleInput : htmlInput).focus();
      return false;
    }
    updateWorking(withDraft());
    finished = true;
    return true;
  }

  function saveRule() {
    if (!commit()) return;
    announce("Rule saved. Remember to save the profile.");
    nav.go(profileHash());
  }

  async function deleteRule() {
    const ok = await confirmAction("Delete this rule?", `The rule for “${ruleName(rule)}” will be removed from the profile.`, "Delete rule");
    if (!ok) return;
    const profile = structuredClone(state.working);
    profile.rules.splice(route.index, 1);
    updateWorking(profile);
    finished = true;
    announce("Rule deleted. Remember to save the profile.");
    nav.go(profileHash());
  }

  // ---- Preview ----
  const frame = previewFrame("Preview of this rule");
  const previewNote = el("p", { class: "hint", role: "status" });
  const codeHolder = el("div");
  const previewBox = el("section", { class: "rule-preview glass", "aria-labelledby": "preview-heading" },
    el("h2", { id: "preview-heading" }, "Preview"), previewNote, frame, el("h3", {}, "HTML produced"), codeHolder);
  let previewToken = 0;

  async function runPreview() {
    const mine = ++previewToken;
    if (validateRule(rule)) {
      previewNote.textContent = "Fix the problem in the form to see a preview.";
      return;
    }
    const profile = withDraft();
    setBusy(previewBox, true);
    try {
      let html;
      let note;
      if (state.previewSource === "document" && state.doc) {
        const found = await extractMatches(state.doc.data, rule, profile);
        if (found.total > 0) {
          html = found.html;
          note = `Showing ${found.shown} of ${found.total} matches in ${state.doc.name}.`;
        } else {
          note = "This rule doesn't match anything in your document, so here it is in the sample instead.";
        }
      } else {
        note = "Showing this rule in the sample document.";
      }
      if (html === undefined) html = (await convert(await buildSpecimen(profile, { only: rule }), profile)).html;
      if (mine !== previewToken) return;
      showPreview(frame, { html, profile, title: "Rule preview" });
      codeHolder.replaceChildren(codeView(html, ""));
      previewNote.textContent = note;
    } catch (error) {
      if (mine === previewToken) previewNote.textContent = `Couldn't build a preview: ${error.message}`;
    } finally {
      if (mine === previewToken) setBusy(previewBox, false);
    }
  }
  const schedulePreview = debounce(runPreview, 300);

  // ---- Layout ----
  const heading = el("h1", {});
  root.append(el("div", { class: "editor-page" },
    el("p", {}, el("a", { href: profileHash() }, `Back to ${state.working.name}`)),
    heading,
    el("div", { class: "rule-layout" },
      el("form", { class: "rule-form glass", onsubmit: (event) => { event.preventDefault(); saveRule(); } },
        field({ id: "rule-type", label: "Type", control: typeSelect }),
        styleField, htmlField, plainField, priorityBox,
        el("div", { class: "row" }, saveButton, cancelButton, deleteButton)),
      previewBox)));
  renderForm();
  runPreview();

  return {
    title: isNew ? "New rule" : `Rule: ${ruleName(rule)}`,
    isDirty: ruleDirty,
    async canLeave() {
      leaveChoice = null;
      if (!ruleDirty()) return true;
      const choice = await chooseUnsaved({ message: `Your changes to the rule “${ruleName(rule) || "new rule"}” haven't been saved.`, saveLabel: "Save rule" });
      if (choice === "cancel") return false;
      if (choice === "save") {
        if (!commit()) return false;
        leaveChoice = "saved";
        return true;
      }
      finished = true;
      leaveChoice = "discarded";
      return true;
    },
    // The router stopped the navigation after this page's guard had already answered.
    leaveCancelled() {
      if (leaveChoice === "discarded") finished = false;
      if (leaveChoice === "saved") {
        // The rule is now in the profile: reopen it cleanly at its real address.
        const index = isNew ? state.working.rules.length - 1 : route.index;
        setTimeout(() => nav.go(`${profileHash()}/rules/${index}`), 0);
      }
      leaveChoice = null;
    },
  };
}
